/**
 * MetabaseList Component
 * 
 * Main list view for Metabase reports. Displays a DataGrid with all reports,
 * supports filtering, sorting, pagination, and navigation to detail view.
 * 
 * Requirements: 1.1, 1.2, 1.3, 1.4, 1.5, 2.1, 2.2, 2.3, 2.4, 2.5, 2.6,
 *               3.1, 3.2, 3.3, 3.4, 4.1, 5.1, 10.1, 10.2, 11.1, 11.4,
 *               12.4, 12.5, 13.1
 */

import { useCallback, useMemo } from 'react';
import { Box } from '@mui/material';
import { GridColDef, GridSortModel, GridRowParams } from '@mui/x-data-grid-pro';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';

import { ListingHeader } from '../components/Header';
import AeronetDataGrid from '../components/datagrid/DataGrid';
import { metabaseApi } from '../services/metabaseApi';
import { MetabaseReport } from '../types/metabase';
import { useCurrentUser } from '../hooks/useCurrentUser';

import type { QueryResults } from '../components/datagrid/DataGrid';

/**
 * MetabaseList Component
 * 
 * Displays a paginated, filterable, sortable list of Metabase reports.
 * Clicking a row navigates to the detail view for that report.
 */
export default function MetabaseList() {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const currentUser = useCurrentUser();
  
  /**
   * DataGrid column definitions
   */
  const columns: GridColDef<MetabaseReport>[] = useMemo(() => [
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      minWidth: 150,
      filterable: false,
    },
    {
      field: 'title',
      headerName: 'Title',
      flex: 1.5,
      minWidth: 200,
      filterable: false,
    },
    {
      field: 'type',
      headerName: 'Type',
      flex: 0.8,
      minWidth: 100,
      filterable: false,
    },
    {
      field: 'metabase_resource_type',
      headerName: 'Resource Type',
      flex: 0.8,
      minWidth: 120,
      filterable: false,
      valueFormatter: (params) => {
        // Capitalize first letter - ensure value is a string
        const value = params.value;
        return value && typeof value === 'string' ? value.charAt(0).toUpperCase() + value.slice(1) : value || '';
      },
    },
    {
      field: 'company',
      headerName: 'Company',
      flex: 1,
      minWidth: 150,
      filterable: false,
      valueGetter: (params) => params.row.company?.name || '',
    },
    {
      field: 'user',
      headerName: 'User',
      flex: 1,
      minWidth: 150,
      filterable: false,
      valueGetter: (params) => params.row.user?.name || '',
    },
    {
      field: 'priority',
      headerName: 'Priority',
      flex: 0.5,
      minWidth: 80,
      type: 'number',
      filterable: false,
    },
    {
      field: 'created_date',
      headerName: 'Created Date',
      flex: 1,
      minWidth: 150,
      type: 'dateTime',
      filterable: false,
      valueGetter: (params) => params.value ? new Date(params.value) : null,
    },
  ], []);

  /**
   * Initial sort configuration
   */
  const initialSort: GridSortModel = useMemo(
    () => [{ field: 'name', sort: 'asc' }] as GridSortModel,
    []
  );

  /**
   * Quick search items for the search field
   */
  const quickSearchItems = useMemo(
    () => [
      {
        field: 'name',
        operator: 'contains',
        label: 'Report Name',
      },
      {
        field: 'title',
        operator: 'contains',
        label: 'Report Title',
      },
      {
        field: 'type',
        operator: 'contains',
        label: 'Type',
      },
    ],
    []
  );

  /**
   * Fetch reports from API with current filters, sort, and pagination
   */
  const getData = useCallback(
    async (params: any) => {
      try {
        // Build API parameters from current state
        const apiParams: any = {
          page: params.page,
          page_size: params.size,
          ...(params.q && { q: params.q }),
          ...(params.filter && { filter: params.filter }),
          ...(params.sort && { sort: params.sort }),
        };
        
        const response = await metabaseApi.list(apiParams);
        
        return {
          total: response.total,
          rows: response.items,
        } as QueryResults<MetabaseReport>;
      } catch (err: any) {
        const errorMessage = err.message || 'Failed to fetch reports';
        enqueueSnackbar(errorMessage, {
          variant: 'error',
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'center',
          },
        });
        throw err;
      }
    },
    [enqueueSnackbar]
  );
  
  /**
   * Handle row click - navigate to detail view
   */
  const handleRowClick = useCallback(
    (params: GridRowParams) => {
      navigate(`/admin/metabase/${params.id}`);
    },
    [navigate]
  );
  
  return (
    <Box height="100%">
      {/* Header with title */}
      <ListingHeader>
        <span>Metabase Reports</span>
      </ListingHeader>
      
      {/* DataGrid with built-in toolbar */}
      <AeronetDataGrid
        columns={columns}
        currentUserId={currentUser?.id || 0}
        data={getData}
        enableAdvancedSearch={true}
        enableCreate={true}
        enableFilter={true}
        // The wrapper defaults `disableColumnFilter=true`, which suppresses MUI's
        // GridToolbarFilterButton regardless of `enableFilter` — both are needed
        // (DataGrid.tsx:133/137; Blank sets the pair at BlankList.tsx:186-187).
        disableColumnFilter={false}
        initialSort={initialSort}
        name="metabase-reports-grid"
        quickSearchItems={quickSearchItems}
        onRowClick={handleRowClick}
        sx={{
          '.MuiDataGrid-row': {
            cursor: 'pointer',
          },
        }}
      />
    </Box>
  );
}
